import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import {
  EMPTY_BETA_REGRESSION_RESULT as EMPTY_BETA_ESTIMATE_RESULT,
  IBetaCalibration,
  IBetaEstimateQuery,
  IBetaEstimateResult
} from '@models/beta/beta-estimate';
import { IBetaRegressionLocalStorage as IBetaEstimateLocalStorage } from '@models/beta/betaRegressionLocalStorage';
import { ILocalStorage } from '@models/localStorage';
import { IRecalculationRequest } from '@models/recalculation/recalculation-request';
import { RecalculationTrigger } from '@models/recalculation/recalculation-trigger';
import { LocalStorageKeys } from '@models/store/local-storage-keys';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { CalibrationSettingsService } from '@services/global/calibration-settings.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MenuService } from '@services/menu.service';
import { ObservableStore } from '@services/observable-store/dist';
import { RecalculationService } from '@services/recalculation/recalculation.service';
import { clone } from '@shared/arrays';
import { getDateString, getLastDayOfMonth } from '@shared/dates';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

declare var require: any;
const uuidv4 = require('uuid/v4');

const NUMBER_LAG_MONTHS = 12;
export const MAX_BETA_REGRESSIONS = 15;

@Injectable({ providedIn: 'root' })
export class BetaEstimateService extends ObservableStore<IStoreState> {
  private betaRegressionEndPoint: string;
  private betaCalibrationEndPoint: string;

  private readonly betaEstimateResults$: BehaviorSubject<IBetaEstimateResult[]>;
  private readonly selectedEstimate$: BehaviorSubject<IBetaEstimateResult>;

  public lagMonths: string[] = [];

  constructor(
    private readonly menu: MenuService,
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly appConfig: AppConfigProvider,
    private readonly settings: CalibrationSettingsService,
    private readonly recalculationService: RecalculationService
  ) {
    super({ trackStateHistory: true });
    this.betaEstimateResults$ = new BehaviorSubject<IBetaEstimateResult[]>([clone(EMPTY_BETA_ESTIMATE_RESULT)]);
    this.selectedEstimate$ = new BehaviorSubject<IBetaEstimateResult>(null);

    // Using number, e.g. '6-Month'
    this.lagMonths = new Array(NUMBER_LAG_MONTHS + 1).fill(null).map((e, index) => `${index}-Month`);
    this.lagMonths[0] = 'None';

    this.appConfig.environment.subscribe(env => {
      this.betaRegressionEndPoint = `${env.API.BETA}/api/beta/estimate`;
      this.betaCalibrationEndPoint = `${env.API.BETA}/api/beta/calibration`;
    });
  }

  get estimateResults(): IBetaEstimateResult[] {
    return this.betaEstimateResults$.value;
  }

  set estimateResults(estimateResults: IBetaEstimateResult[]) {
    this.betaEstimateResults$.next(estimateResults);
  }

  get onEstimatesChanged$(): Observable<IBetaEstimateResult[]> {
    return this.betaEstimateResults$.asObservable();
  }

  set selectedEstimate(estimateResult: IBetaEstimateResult) {
    if (estimateResult === null) {
      this.selectedEstimate$.next(null);
    } else {
      const estimate = this.estimateResults.find(({ Id }) => Id === estimateResult.Id);
      this.selectedEstimate$.next(estimate);
    }
  }

  get selectedEstimate() {
    return this.estimateResults.find(({ Id }) => Id === (this.selectedEstimate$.getValue() ? this.selectedEstimate$.getValue().Id : ''));
  }

  get onSelectedEstimateChanged$(): Observable<IBetaEstimateResult> {
    return this.selectedEstimate$.asObservable();
  }

  get savedBetaEstimateFromStore(): IBetaCalibration {
    return this.getState().savedBetaCalibration;
  }

  set savedBetaEstimateFromStore(betaCalibration: IBetaCalibration) {
    this.setState({ savedBetaCalibration: betaCalibration }, StoreActions.GetSavedBetaRegressions);
  }

  public addOrUpdateEstimate(estimate: IBetaEstimateResult): Observable<IBetaEstimateResult> {
    // Ensuring dates are always first and last of the month

    const startDate = getLastDayOfMonth(new Date(estimate.PeriodStartDate));
    const endDate = getLastDayOfMonth(new Date(estimate.PeriodEndDate));

    const PeriodStartDate = getDateString(startDate);
    const PeriodEndDate = getDateString(endDate);

    const { MarketRateId, ProductId, Lag } = estimate;

    const query: IBetaEstimateQuery = {
      MarketRateId,
      ProductId,
      PeriodStartDate,
      PeriodEndDate,
      Lag
    };

    estimate.Running = true;

    return this.http.post(this.betaRegressionEndPoint, query).pipe(
      map((estimateResult: IBetaEstimateResult) => {
        if (estimate.Id) {
          // Updating existing estimate
          estimateResult = { ...estimate, ...estimateResult };
          estimateResult.Pristine = true;
          estimateResult.Modified = true;
          delete estimateResult.Running;
          const index = this.estimateResults.findIndex(({ Id }) => estimateResult.Id === Id);
          const estimates = clone(this.estimateResults);
          estimates[index] = estimateResult;
          this.estimateResults = [];
          this.estimateResults = estimates;
        } else {
          // Creating new estimate

          // Step One Removing the new estimate from the list
          this.estimateResults = this.estimateResults.filter(({ Id }) => Id);

          estimateResult.Id = uuidv4();
          estimateResult.Modified = true;
          estimateResult.Pristine = true;
          this.estimateResults.push(estimateResult, clone(EMPTY_BETA_ESTIMATE_RESULT));
          this.estimateResults = this.setEstimateNames(this.estimateResults);

          // Setting this item as the selected estimate
          this.selectedEstimate = estimateResult;
        }
        this.localStorageService.storeInLocalStorage<IBetaEstimateLocalStorage[]>(
          this.estimateResults.filter(({ Id }) => Id),
          LocalStorageKeys.Beta,
          this.menu.selectedProduct.ProductId
        );
        this.selectedEstimate$.next(clone(estimateResult));
        return estimateResult;
      })
    );
  }

  public removeEstimate(estimate: IBetaEstimateResult): void {
    if (estimate && estimate.Id) {
      this.estimateResults = this.estimateResults.filter(({ Id }) => Id !== estimate.Id);
      this.estimateResults = this.setEstimateNames(this.estimateResults);
      this.localStorageService.storeInLocalStorage<IBetaEstimateLocalStorage[]>(
        this.estimateResults.filter(({ Id }) => Id),
        LocalStorageKeys.Beta,
        this.menu.selectedProduct.ProductId
      );
    }
  }

  private cleanEstimates(estimates: IBetaEstimateResult[]): IBetaEstimateResult[] {
    estimates = this.setEstimateNames(estimates);
    estimates = this.setEstimateIds(estimates);

    const emptyEstimate = clone(EMPTY_BETA_ESTIMATE_RESULT);
    emptyEstimate.Id = '';
    emptyEstimate.Lag = 0;
    emptyEstimate.MarketRateId = 0;
    estimates.push(emptyEstimate);
    return estimates;
  }

  private setEstimateIds(estimates: IBetaEstimateResult[]): IBetaEstimateResult[] {
    estimates.forEach(estimate => {
      if (!estimate.Id) {
        estimate.Id = uuidv4();
      }
    });

    return estimates;
  }

  private setEstimateNames(estimates: IBetaEstimateResult[]): IBetaEstimateResult[] {
    estimates
      .filter(({ Id }) => Id)
      .forEach((estimate, index) => {
        estimate.Name = `Estimate ${index + 1}`;
      });

    return estimates;
  }

  public async saveBetaCalibration(betaCalibration: IBetaCalibration) {
    const calibrationEndDate = await this.settings.getSettingByName('CALIBRATION_END_DATE');
    const forecastEndDate = await this.settings.getSettingByName('FORECAST_END_DATE');

    const requestObject = {
      ...betaCalibration,
      calibrationEndDate: calibrationEndDate.SettingValue,
      forecastEndDate: forecastEndDate.SettingValue
    };

    requestObject.DateTimeCreated = '';
    requestObject.DateTimeModified = '';

    return this.http
      .post(this.betaCalibrationEndPoint, requestObject)
      .pipe(
        tap((res: IBetaCalibration) => {
          this.savedBetaEstimateFromStore = res;
          // Discarding the local storage
          this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Beta, this.menu.selectedProduct.ProductId);
          // Setting the modified to false
          this.estimateResults.forEach(estimate => delete estimate.Modified);
          // RECALC CALL SAVE -- FOR REVIEW
          if (betaCalibration.ProductId) {
            let trigger: RecalculationTrigger = null;
            const productIds: number[] = [];
            trigger = RecalculationTrigger.CalibrationSave;
            if (trigger !== null) {
              productIds.push(betaCalibration.ProductId);

              const request: IRecalculationRequest = {
                ProductIds: productIds,
                RecalculationTrigger: trigger
              };

              this.recalculationService
                .startCalculation(request)
                .toPromise()
                .catch(err => {
                  console.warn(`Error recalculating calibrations`, { request, error: err });
                });
            }
          }
        })
      )
      .toPromise();
  }

  /**
   * Updates a single estimate in the list. The id of the estimate that is
   * passed must have the same ID as a estimate in the list
   * @param estimate The updated estimate
   * @returns A boolean indicating wether the estimate was updated or not
   */
  public updateEstimateInList(estimate: IBetaEstimateResult): Promise<boolean> {
    const index = this.estimateResults.findIndex(({ Id }) => Id === estimate.Id);
    if (index >= 0) {
      this.estimateResults[index] = estimate;
      return Promise.resolve(true);
    } else {
      return Promise.resolve(false);
    }
  }

  public async getSavedBetaEstimates(productId: number, clearSelectedEstimate = true): Promise<IBetaEstimateResult[]> {
    const results = await forkJoin([
      of(this.localStorageService.fetchFromLocalStorage<IBetaEstimateResult[]>(LocalStorageKeys.Beta, productId)),
      this.fetchLatestSavedCalibrationFromDB(productId)
    ]).toPromise();

    const calibrationLS = results[0] as ILocalStorage<IBetaEstimateResult[]>;

    const calibrationDB: IBetaCalibration = results[1] as IBetaCalibration;
    if (calibrationDB && calibrationDB.Regressions === '') {
      calibrationDB.Regressions = '[]';
    }
    const estimatesLS: IBetaEstimateResult[] = calibrationLS ? calibrationLS.payload : null;
    const estimatesDB: IBetaEstimateResult[] = calibrationDB ? JSON.parse(calibrationDB.Regressions) : null;

    let chosenEstimates: IBetaEstimateResult[] = [];

    if (calibrationLS) {
      if (calibrationDB) {
        // Calibration saved in both DB and LS

        // Storing the calibration on the state
        this.savedBetaEstimateFromStore = calibrationDB;

        const dateLocalStorage = new Date(calibrationLS.storeDate);
        const dateDB = new Date(calibrationDB.DateTimeModified || calibrationDB.DateTimeCreated);

        if (dateLocalStorage > dateDB) {
          chosenEstimates = estimatesLS;
        } else {
          chosenEstimates = estimatesDB;
          this.discardLocalChanges(productId);
        }
      } else {
        // Calibration saved only in LS
        chosenEstimates = estimatesLS;
      }
    } else {
      if (calibrationDB) {
        // Calibration saved only in DB

        this.savedBetaEstimateFromStore = calibrationDB;

        estimatesDB.forEach(estimate => {
          delete estimate.Running;
          delete estimate.Modified;
          estimate.Pristine = true;
        });

        chosenEstimates = estimatesDB;
      } else {
        // Nothing saved
        // Already empty
      }
    }

    chosenEstimates = this.cleanEstimates(chosenEstimates);
    this.estimateResults = chosenEstimates;
    if (clearSelectedEstimate) {
      this.clearSelectedEstimate();
    }

    return of(chosenEstimates).toPromise();
  }

  private clearSelectedEstimate() {
    // Setting the selected estimates
    const emptyEstimate = this.estimateResults.find(({ Id }) => !Id);
    this.selectedEstimate = emptyEstimate;
  }

  private fetchLatestSavedCalibrationFromDB(productId: number): Observable<IBetaCalibration> {
    return this.http.get<IBetaCalibration[]>(`${this.betaCalibrationEndPoint}/forproduct/${productId}`).pipe(
      map(calibrations => {
        // No guarantee calibrations are rodered, so before taking latest, reorder them by ID or Date
        calibrations.sort((a, b) => {
          if (a.BetaCalibrationId && b.BetaCalibrationId) {
            return a.BetaCalibrationId - b.BetaCalibrationId;
          } else {
            return +new Date(a.DateTimeModified || a.DateTimeCreated) - +new Date(b.DateTimeModified || a.DateTimeCreated);
          }
        });
        return calibrations[calibrations.length - 1];
      })
    );
  }

  public discardLocalChanges(productId: number, clearSelectedEstimate = true) {
    this.localStorageService.clearFromLocalStorage(LocalStorageKeys.Beta, productId);
    this.getSavedBetaEstimates(productId, clearSelectedEstimate);
  }

  public canDeactivate(): boolean {
    const estimatesFromStorage = this.localStorageService.fetchFromLocalStorage<IBetaEstimateResult[]>(
      LocalStorageKeys.Beta,
      this.menu.selectedProduct.ProductId
    );

    if (estimatesFromStorage && estimatesFromStorage.payload && estimatesFromStorage.payload.length > 0) {
      return false;
    }

    return true;
  }
}
